import * as React from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  Card,
  Icon,
  StackLayout,
  StatusBadge,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { getEntityStatusBadgeProps } from '@web-config-app/core';
import type { SyncEnvironment } from './sync-mode.types';

interface SyncDirectionBarProps {
  sourceEnvironment: SyncEnvironment;
  targetEnvironment: SyncEnvironment;
}

export const SyncDirectionBar = ({
  sourceEnvironment,
  targetEnvironment,
}: SyncDirectionBarProps) => {
  const { formatMessage } = useIntl();

  const sourceStatusBadgeProps = sourceEnvironment.entityStatus
    ? getEntityStatusBadgeProps(sourceEnvironment.entityStatus)
    : undefined;
  const targetStatusBadgeProps = targetEnvironment.entityStatus
    ? getEntityStatusBadgeProps(targetEnvironment.entityStatus)
    : undefined;

  return (
    <StackLayout
      orientation="horizontal"
      horizontalAlignment="center"
      verticalAlignment="center"
      spacing="$one"
      css={{ padding: '$one $oneAndHalf' }}
    >
      <Card.Flat
        css={{
          padding: '$half $one',
          border: '$borderWidths$thin solid $interactiveActionPrimary',
          backgroundColor: '$surfaceBackgroundPrimary',
          textAlign: 'center',
        }}
      >
        <UtilityText
          emphasis="emphasized"
          css={{
            fontSize: '$caption',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '$interactiveActionPrimary',
          }}
        >
          {formatMessage({ id: 'SYNC_DIRECTION_SOURCE_LABEL' })}
        </UtilityText>
        <StackLayout
          orientation="horizontal"
          spacing="$half"
          verticalAlignment="center"
          horizontalAlignment="center"
        >
          <UtilityText emphasis="emphasized">
            {sourceEnvironment.name}
          </UtilityText>
          {sourceStatusBadgeProps && (
            <StatusBadge
              status={sourceStatusBadgeProps.genesisStatus}
              label={formatMessage({ id: sourceStatusBadgeProps.labelKey })}
            />
          )}
        </StackLayout>
      </Card.Flat>

      <Icon icon="interfaceArrowRight" size="$oneAndHalf" aria-hidden="true" />

      <Card.Flat
        css={{
          padding: '$half $one',
          border: '$borderWidths$thin solid $onSurfaceTextCritical',
          backgroundColor: '$surfaceBackgroundPrimary',
          textAlign: 'center',
        }}
      >
        <UtilityText
          emphasis="emphasized"
          css={{
            fontSize: '$caption',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '$onSurfaceTextCritical',
          }}
        >
          {formatMessage({ id: 'SYNC_DIRECTION_TARGET_LABEL' })}
        </UtilityText>
        <StackLayout
          orientation="horizontal"
          spacing="$half"
          verticalAlignment="center"
          horizontalAlignment="center"
        >
          <UtilityText emphasis="emphasized">
            {targetEnvironment.name}
          </UtilityText>
          {targetStatusBadgeProps && (
            <StatusBadge
              status={targetStatusBadgeProps.genesisStatus}
              label={formatMessage({ id: targetStatusBadgeProps.labelKey })}
            />
          )}
        </StackLayout>
      </Card.Flat>
    </StackLayout>
  );
};
